import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import { Color, type Mesh, type MeshBasicMaterial } from 'three'
import { amortecer, amortecerCor, prefereMovimentoReduzido } from '../componentes/movimento'

const CIANO = new Color('#38bdf8')
const LARANJA = new Color('#f97316')

/** Tempo em que o halo aparece, e em que muda de cor ou de opacidade, até assentar. */
const TEMPO_DE_APARICAO_S = 0.3
const TEMPO_DE_TROCA_S = 0.22
/** O halo nasce menor e cresce até o tamanho do pulso. */
const ESCALA_AO_APARECER = 0.55

const rgb = (c: Color): [number, number, number] => [c.r, c.g, c.b]

/** Indicador puramente visual do ponto que a demonstração está explicando. */
export function HaloZonaCorte3D({
  posicao,
  rotacao = [0, Math.PI / 2, 0],
  executando,
  recuando,
  pausado,
}: {
  posicao: readonly [number, number, number]
  rotacao?: readonly [number, number, number]
  executando: boolean
  recuando: boolean
  pausado: boolean
}) {
  const grupo = useRef<Mesh>(null)
  const anel = useRef<MeshBasicMaterial>(null)
  const nucleo = useRef<MeshBasicMaterial>(null)
  const movimentoReduzido = useMemo(prefereMovimentoReduzido, [])

  // O que a tela mostra agora, que persegue o que a demonstração pede: sem isso
  // o halo surgia de repente e trocava de cor e de opacidade num quadro só.
  const exibido = useRef<{ presenca: number; opacidade: number; cor: [number, number, number] } | null>(null)

  useFrame(({ clock }, delta) => {
    const alvoOpacidade = recuando ? 0.28 : 0.8
    const alvoCor = rgb(executando ? LARANJA : CIANO)
    // começa invisível e já na cor e na opacidade certas: só a presença é que cresce
    const atual = (exibido.current ??= { presenca: 0, opacidade: alvoOpacidade, cor: alvoCor })

    atual.presenca = amortecer(atual.presenca, 1, TEMPO_DE_APARICAO_S, delta, movimentoReduzido)
    atual.opacidade = amortecer(atual.opacidade, alvoOpacidade, TEMPO_DE_TROCA_S, delta, movimentoReduzido)
    atual.cor = amortecerCor(atual.cor, alvoCor, TEMPO_DE_TROCA_S, delta, movimentoReduzido)

    const pulso = pausado || movimentoReduzido ? 1 : 0.9 + Math.sin(clock.elapsedTime * 4) * 0.12
    const crescimento = ESCALA_AO_APARECER + (1 - ESCALA_AO_APARECER) * atual.presenca
    if (grupo.current) grupo.current.scale.setScalar(pulso * crescimento)
    if (anel.current) {
      anel.current.color.setRGB(...atual.cor)
      anel.current.opacity = atual.opacidade * atual.presenca
    }
    if (nucleo.current) {
      nucleo.current.color.setRGB(...atual.cor)
      nucleo.current.opacity = atual.opacidade * 0.3 * atual.presenca
    }
  })

  return (
    <mesh ref={grupo} position={posicao} rotation={rotacao} renderOrder={2}>
      <ringGeometry args={[0.13, 0.18, 32]} />
      <meshBasicMaterial ref={anel} transparent depthWrite={false} toneMapped={false} />
      <mesh position={[0, 0, -0.003]}>
        <circleGeometry args={[0.08, 32]} />
        <meshBasicMaterial ref={nucleo} transparent depthWrite={false} toneMapped={false} />
      </mesh>
    </mesh>
  )
}
